import { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import api from '@/utils/api';
import { toast } from 'sonner';
import {
  User, Mail, Phone, FileText, MapPin, Calendar, Save, ChevronRight, LogOut, Settings
} from 'lucide-react';

export default function UserProfile() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [form, setForm] = useState({
    full_name: '',
    email: '',
    phone: '',
    passport_number: '',
    birth_date: '',
    address: '',
    city: '',
    bio: '',
  });

  useEffect(() => {
    if (!user) {
      navigate('/login');
      return;
    }
    setForm({
      full_name: user.full_name || '',
      email: user.email || '',
      phone: user.phone || '',
      passport_number: user.passport_number || '',
      birth_date: user.birth_date || '',
      address: user.address || '',
      city: user.city || '',
      bio: user.bio || '',
    });
  }, [user, navigate]);

  const handleChange = (field, value) => {
    setForm(prev => ({ ...prev, [field]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const { email, ...updateData } = form; // Don't update email
      await api.put('/auth/me', updateData);
      toast.success('Perfil actualizado com sucesso!');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erro ao actualizar perfil');
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = () => {
    logout();
    navigate('/');
    toast.success('Sessão terminada');
  };

  if (!user) return null;

  const inputCls = "w-full pl-10 pr-3 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#CC0000] focus:border-[#CC0000] outline-none text-sm";
  const labelCls = "block text-sm font-medium text-gray-700 mb-1";

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-[#0A0A0A] text-white">
        <div className="max-w-4xl mx-auto px-4 py-10">
          <div className="flex items-center gap-2 text-xs text-gray-400 mb-3">
            <Link to="/" className="hover:text-white">Início</Link>
            <ChevronRight size={12} />
            <span className="text-white">Minha Conta</span>
          </div>
          <h1 className="text-2xl md:text-3xl font-bold">Minha Conta</h1>
          <p className="text-gray-400 text-sm mt-1">Gerencie os seus dados pessoais</p>
        </div>
      </div>

      <div className="max-w-4xl mx-auto px-4 py-8">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {/* Sidebar */}
          <div className="md:col-span-1">
            <div className="bg-white rounded-xl border border-gray-200 p-5">
              {/* User Avatar */}
              <div className="text-center mb-6">
                <div className="w-20 h-20 bg-gradient-to-br from-[#CC0000] to-[#8B0000] rounded-full flex items-center justify-center mx-auto mb-3">
                  <span className="text-white text-2xl font-bold">
                    {user.full_name?.charAt(0)?.toUpperCase() || 'U'}
                  </span>
                </div>
                <h3 className="font-bold text-gray-900">{user.full_name}</h3>
                <p className="text-sm text-gray-500">{user.email}</p>
              </div>

              {/* Quick Links */}
              <nav className="space-y-1">
                <Link to="/minha-conta" className="flex items-center gap-3 px-4 py-2.5 bg-red-50 text-[#CC0000] rounded-lg font-medium text-sm">
                  <User size={16} /> Meus Dados
                </Link>
                <Link to="/meus-agendamentos" className="flex items-center gap-3 px-4 py-2.5 text-gray-600 hover:bg-gray-50 rounded-lg font-medium text-sm">
                  <Calendar size={16} /> Meus Agendamentos
                </Link>
                <Link to="/meus-pedidos" className="flex items-center gap-3 px-4 py-2.5 text-gray-600 hover:bg-gray-50 rounded-lg font-medium text-sm">
                  <FileText size={16} /> Meus Pedidos
                </Link>
                <button
                  onClick={handleLogout}
                  className="w-full flex items-center gap-3 px-4 py-2.5 text-red-600 hover:bg-red-50 rounded-lg font-medium text-sm"
                >
                  <LogOut size={16} /> Terminar Sessão
                </button>
              </nav>
            </div>
          </div>

          {/* Main Content */}
          <div className="md:col-span-2">
            <form onSubmit={handleSubmit} className="bg-white rounded-xl border border-gray-200 p-6">
              <h2 className="text-lg font-bold text-gray-900 mb-6 flex items-center gap-2">
                <Settings size={20} className="text-[#CC0000]" />
                Dados Pessoais
              </h2>

              <div className="space-y-5">
                {/* Nome Completo */}
                <div>
                  <label className={labelCls}>Nome Completo</label>
                  <div className="relative">
                    <User size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                    <input
                      type="text"
                      value={form.full_name}
                      onChange={e => handleChange('full_name', e.target.value)}
                      className={inputCls}
                      placeholder="O seu nome completo"
                    />
                  </div>
                </div>

                {/* Email (readonly) */}
                <div>
                  <label className={labelCls}>Email</label>
                  <div className="relative">
                    <Mail size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                    <input
                      type="email"
                      value={form.email}
                      readOnly
                      className={`${inputCls} bg-gray-50 cursor-not-allowed`}
                    />
                  </div>
                  <p className="text-xs text-gray-400 mt-1">O email não pode ser alterado</p>
                </div>

                {/* Telefone */}
                <div>
                  <label className={labelCls}>Telefone</label>
                  <div className="relative">
                    <Phone size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                    <input
                      type="tel"
                      value={form.phone}
                      onChange={e => handleChange('phone', e.target.value)}
                      className={inputCls}
                      placeholder="+34 600 000 000"
                    />
                  </div>
                </div>

                {/* Passaporte */}
                <div>
                  <label className={labelCls}>Número do Passaporte</label>
                  <div className="relative">
                    <FileText size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                    <input
                      type="text"
                      value={form.passport_number}
                      onChange={e => handleChange('passport_number', e.target.value)}
                      className={inputCls}
                      placeholder="N123456789"
                    />
                  </div>
                </div>

                {/* Data de Nascimento */}
                <div>
                  <label className={labelCls}>Data de Nascimento</label>
                  <div className="relative">
                    <Calendar size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                    <input
                      type="date"
                      value={form.birth_date}
                      onChange={e => handleChange('birth_date', e.target.value)}
                      className={inputCls}
                    />
                  </div>
                </div>

                {/* Morada */}
                <div>
                  <label className={labelCls}>Morada</label>
                  <div className="relative">
                    <MapPin size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                    <input
                      type="text"
                      value={form.address}
                      onChange={e => handleChange('address', e.target.value)}
                      className={inputCls}
                      placeholder="Calle Mayor, 123"
                    />
                  </div>
                </div>

                {/* Cidade */}
                <div>
                  <label className={labelCls}>Cidade</label>
                  <div className="relative">
                    <MapPin size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                    <input
                      type="text"
                      value={form.city}
                      onChange={e => handleChange('city', e.target.value)}
                      className={inputCls}
                      placeholder="Madrid"
                    />
                  </div>
                </div>
              </div>

              {/* Submit Button */}
              <div className="mt-8 flex justify-end">
                <button
                  type="submit"
                  disabled={loading}
                  className="flex items-center gap-2 px-6 py-2.5 bg-[#CC0000] text-white font-medium rounded-lg hover:bg-[#A30000] disabled:opacity-50 transition-colors"
                >
                  {loading ? (
                    <>
                      <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                      A guardar...
                    </>
                  ) : (
                    <>
                      <Save size={16} />
                      Guardar Alterações
                    </>
                  )}
                </button>
              </div>
            </form>

            {/* Info Box */}
            <div className="mt-4 p-4 bg-blue-50 rounded-lg border border-blue-200">
              <p className="text-sm text-blue-800">
                <strong>Dica:</strong> Ao preencher os seus dados pessoais, estes serão automaticamente utilizados nos formulários de agendamento e pedidos de serviços consulares.
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
